import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Link from '@/components/UI/Link';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import type { StudyDraft } from '@/model/Draft.model';
import Http from '@/support/http';
import { api as apiRoutes, web as webRoutes } from '@/support/route';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

const columns: Array<TanStackColumnDef<StudyDraft>> = [
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    enableSorting: true,
    isVisible: true,
    cell: ({ row }: { row: { original: StudyDraft } }) => (
      <Link
        className="link"
        to={webRoutes('studies.new.draft', {
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          formId: row.original.config.form_id || '',
          draftId: row.original.id,
        })}
      >
        {row.original.name}
      </Link>
    ),
  },
  {
    id: 'created_at',
    header: 'Created',
    accessorKey: 'created_at',
    enableSorting: true,
    isVisible: true,
    cell: ({ row }: { row: { original: StudyDraft } }) => <DateTimeRenderer value={row.original.created_at} />,
  },
  {
    id: 'updated_at',
    header: 'Updated',
    accessorKey: 'updated_at',
    enableSorting: true,
    isVisible: true,
    cell: ({ row }: { row: { original: StudyDraft } }) => <DateTimeRenderer value={row.original.updated_at} />,
  },
];

const DraftsTable: React.FC = () => {
  const dispatch = useDispatch();

  const handleDraftsDelete = async ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { fetchTableData, setUpdating, setApiError },
    },
  }: UseTableProps<StudyDraft>) => {
    setUpdating(true);
    const draftIDs = selectedFlatRows.map(({ original: { id } }) => id);
    try {
      await Http.delete(apiRoutes('drafts.studies.bulkDelete'), {
        data: { drafts_ids: draftIDs },
      });
      await fetchTableData();
      toast.success(`Deleted ${draftIDs.length} draft${draftIDs.length !== 1 ? 's' : ''}`);
    } catch (error) {
      setApiError(error as Error);
    } finally {
      setUpdating(false);
    }
  };

  const bulkActions = (props: UseTableProps<StudyDraft>) => [
    [
      {
        name: 'Delete',
        className: 'red',
        action: () => {
          dispatch({
            type: 'OPEN_MODAL',
            modal: 'CONFIRM_DELETE_STUDY_FORM_SECTION',
            props: {
              onClick: () => handleDraftsDelete(props),
            },
          });
        },
      },
    ],
  ];

  return (
    <div className="pa4">
      <APITable
        columns={columns}
        apiInfo={{ type: 'legacyInternalApi', route: apiRoutes('drafts.studies') }}
        bulkActions={bulkActions}
        searchPlaceholderText="Search by study name"
      />
    </div>
  );
};

export default DraftsTable;
